import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AccessiSocial } from "@/components/auth/AccessiSocial";
import { destinazioneDopoAccesso } from "@/lib/dopoAccesso";
import { registrazioneNellApp } from "@/lib/iscrizione";
import { codiceCompleto, datiDalLink, entraConCodice, mandaCodice, pulisciCodice, verificaEmail } from "@/lib/accessoCodice";

/**
 * L'accesso in due passi (06/10/2026, Davide): prima l'email; poi, se
 * l'account c'e', la password (o un codice via email); se non c'e', lo si dice
 * e si propone l'iscrizione. Chi e' stato iscritto da Datacom al banco o a un
 * evento entra col codice che gli e' arrivato e subito dopo sceglie la sua
 * password (/imposta-password).
 *
 * Dal pulsante della mail (#email=…&codice=…) si arriva gia' al passo del
 * codice, con email e codice scritti: resta da premere «Entra».
 *
 * Se la verifica dell'email non risponde si passa alla password come prima:
 * l'accesso non dipende dalla funzione nuova.
 */
type Passo = "email" | "password" | "codice" | "nessuno";

const ATTESA_NUOVO_CODICE_S = 30;

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  // Letto una volta sola, all'apertura: poi il «#» si toglie dall'indirizzo.
  const [dalLink] = useState(() => datiDalLink(location.hash));
  // Il pulsante della mail porta il codice: si va dritti al codice. Il link
  // per WhatsApp no (14/10): si parte dall'email gia' scritta, e «Continua»
  // manda un codice fresco.
  const [passo, setPasso] = useState<Passo>(dalLink?.codice ? "codice" : "email");
  const [email, setEmail] = useState(dalLink?.email ?? "");
  const [password, setPassword] = useState("");
  const [codice, setCodice] = useState(dalLink?.codice ?? "");
  /** Il codice e' arrivato con la mail di benvenuto (account creato da Datacom), non chiesto qui. */
  const [codiceDiBenvenuto, setCodiceDiBenvenuto] = useState(!!dalLink?.codice && dalLink.benvenuto);
  const [attesa, setAttesa] = useState(0);
  const [loading, setLoading] = useState(false);
  /**
   * IL CODICE DEL PULSANTE E' SCADUTO (14/10/2026). I funzionari dei
   * costruttori aprivano la mail di benvenuto il giorno dopo: il codice non
   * valeva piu' e «Codice sbagliato o scaduto» li lasciava li'. Il codice del
   * pulsante non puo' essere sbagliato, quindi se Supabase lo rifiuta e'
   * scaduto (o gia' usato): se ne manda uno nuovo da qui, UNA volta, e lo si
   * dice. Parte solo dal clic su «Entra»: i filtri della posta che aprono i
   * link da soli non lo fanno scattare.
   */
  const [nuovoDaSolo, setNuovoDaSolo] = useState(false);
  const [avviso, setAvviso] = useState<string | null>(null);

  // Email e codice non restano nella cronologia ne' nei segnalibri.
  useEffect(() => {
    if (!location.hash) return;
    navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: location.state });
    // solo all'apertura
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (attesa <= 0) return;
    const t = setTimeout(() => setAttesa((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [attesa]);

  const dopoAccesso = () => navigate(destinazioneDopoAccesso(location.state), { replace: true });

  const cambiaEmail = () => {
    setPasso("email");
    setPassword("");
    setCodice("");
    setAvviso(null);
  };

  const onContinua = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const esito = await verificaEmail(email);
      if (esito.tipo === "nessuno") setPasso("nessuno");
      else if (esito.tipo === "codice") {
        // ACCOUNT CREATO DA UN ADMIN, PASSWORD NON ANCORA SCELTA (Davide, 14/10:
        // «mette la mail e il sistema si accorge che e' un account creato e gli
        // manda in automatico il codice»). Il codice della mail di benvenuto
        // il giorno dopo e' scaduto: se ne manda uno fresco adesso, dal clic
        // su «Continua». Se l'invio non riesce, si torna a com'era: il codice
        // della mail di benvenuto e «Mandane uno nuovo».
        const errore = await mandaCodice(email);
        setCodice("");
        if (errore) {
          toast.error(errore);
          setCodiceDiBenvenuto(true);
        } else {
          setCodiceDiBenvenuto(false);
          setAttesa(ATTESA_NUOVO_CODICE_S);
          setAvviso(
            `Ti abbiamo appena mandato il codice a ${email.trim()}: apri la mail «Il tuo codice per entrare in Datacom AI» (guarda anche nello spam) e scrivilo qui sotto. Subito dopo scegli la tua password.`,
          );
        }
        setPasso("codice");
      } else setPasso("password");
    } finally {
      setLoading(false);
    }
  };

  const onPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
      if (error) {
        toast.error("Password non corretta", { description: "Riprova, oppure entra con un codice via email." });
        return;
      }
      dopoAccesso();
    } finally {
      setLoading(false);
    }
  };

  const chiediCodice = async () => {
    if (loading || attesa > 0) return;
    setLoading(true);
    try {
      const errore = await mandaCodice(email);
      if (errore) {
        toast.error(errore);
        return;
      }
      toast.success("Codice inviato", { description: `Controlla la posta di ${email.trim()}` });
      setAvviso(null);
      setCodiceDiBenvenuto(false);
      setCodice("");
      setAttesa(ATTESA_NUOVO_CODICE_S);
      setPasso("codice");
    } finally {
      setLoading(false);
    }
  };

  const onCodice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || !codiceCompleto(codice)) return;
    setLoading(true);
    try {
      const { errore, daImpostare, rifiutato } = await entraConCodice(email, codice);
      if (errore) {
        const delPulsante = !!dalLink?.codice && codice === dalLink.codice;
        if (rifiutato && delPulsante && !nuovoDaSolo) {
          setNuovoDaSolo(true);
          if (!(await mandaCodice(email))) {
            setCodiceDiBenvenuto(false);
            setCodice("");
            setAttesa(ATTESA_NUOVO_CODICE_S);
            setAvviso(`Il codice di quella mail non vale più: te ne abbiamo appena mandato uno nuovo a ${email.trim()}. Controlla la posta (anche lo spam) e scrivilo qui sotto.`);
            return;
          }
        }
        toast.error(errore);
        return;
      }
      if (daImpostare) navigate("/imposta-password", { replace: true });
      else dopoAccesso();
    } finally {
      setLoading(false);
    }
  };

  /** L'email scelta, con «Cambia»: nei passi dopo il primo non si riscrive. */
  const emailScelta = (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background/60 px-3 py-2">
      <span className="min-w-0 truncate text-sm text-foreground">{email.trim()}</span>
      <button type="button" onClick={cambiaEmail} className="shrink-0 text-xs text-primary hover:underline">
        Cambia
      </button>
    </div>
  );

  return (
    <AuthLayout title="Accedi">
      {passo === "email" && (
        <form onSubmit={onContinua} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="username"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <Button type="submit" className="w-full bg-accent text-accent-foreground hover:bg-accent/90" disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continua"}
          </Button>

          {/* Sull'iPhone solo l'accesso (Apple 3.1.1, quarto rifiuto, 05/10/2026):
              niente «Registrati» e niente Google/Apple, che al primo accesso
              CREANO l'account. Resta cio' che si usa senza account. */}
          {registrazioneNellApp() ? (
            <>
              <AccessiSocial />

              <p className="pt-2 text-center text-sm text-muted-foreground">
                Non hai un account?{" "}
                <Link to="/signup" className="text-primary hover:underline">
                  Registrati
                </Link>
              </p>
            </>
          ) : (
            <p className="pt-2 text-center text-sm text-muted-foreground">
              Gli{" "}
              <Link to="/strumenti" className="text-primary hover:underline">
                strumenti di calcolo
              </Link>{" "}
              si usano anche senza account.
            </p>
          )}
        </form>
      )}

      {passo === "password" && (
        <form onSubmit={onPassword} className="space-y-4">
          {emailScelta}
          {/* Per i gestori di password: l'email accanto alla password che salvano. */}
          <input type="email" autoComplete="username" value={email.trim()} readOnly tabIndex={-1} className="sr-only" />
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Link to="/forgot-password" className="text-xs text-primary hover:underline">
                Password dimenticata?
              </Link>
            </div>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <Button type="submit" className="w-full bg-accent text-accent-foreground hover:bg-accent/90" disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accedi"}
          </Button>
          <Button type="button" variant="outline" className="w-full" onClick={chiediCodice} disabled={loading}>
            Entra con un codice via email
          </Button>
        </form>
      )}

      {passo === "codice" && (
        <form onSubmit={onCodice} className="space-y-4">
          {emailScelta}
          {avviso && (
            <p role="status" className="rounded-lg border border-primary/40 bg-primary/10 px-3 py-2 text-sm text-foreground">
              {avviso}
            </p>
          )}
          {!avviso && (
            <p className="text-sm text-muted-foreground">
              {codiceDiBenvenuto
                ? "Il tuo account l'ha creato Datacom: scrivi il codice della mail «Il tuo account Datacom AI è pronto». Subito dopo scegli la tua password."
                : "Scrivi il codice che ti abbiamo appena mandato per email."}
            </p>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="codice">Codice</Label>
            <Input
              id="codice"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              value={codice}
              onChange={(e) => setCodice(pulisciCodice(e.target.value))}
              className="text-center font-mono text-lg tracking-[0.3em]"
              required
            />
          </div>
          <Button
            type="submit"
            className="w-full bg-accent text-accent-foreground hover:bg-accent/90"
            disabled={loading || !codiceCompleto(codice)}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Entra"}
          </Button>
          <p className="text-center text-sm text-muted-foreground">
            {attesa > 0 ? (
              <>Nuovo codice fra {attesa} s</>
            ) : (
              <>
                {codiceDiBenvenuto ? "Non trovi la mail? " : "Non è arrivato? "}
                <button type="button" onClick={chiediCodice} className="text-primary hover:underline" disabled={loading}>
                  Mandane uno nuovo
                </button>
              </>
            )}
          </p>
        </form>
      )}

      {passo === "nessuno" && (
        <div className="space-y-4">
          {emailScelta}
          <p className="text-sm text-foreground">Con questa email non c&apos;è un account.</p>
          {registrazioneNellApp() ? (
            <Button asChild className="w-full bg-accent text-accent-foreground hover:bg-accent/90">
              <Link to="/signup">Iscriviti</Link>
            </Button>
          ) : (
            // Sull'iPhone niente «chiedi l'account a Datacom»: per Apple (3.1.1,
            // 29/09) si legge come un invito a comprare fuori dall'app.
            <p className="text-sm text-muted-foreground">Controlla di averla scritta bene.</p>
          )}
          <p className="text-center text-sm text-muted-foreground">
            Hai già un account con questa email?{" "}
            <button type="button" onClick={() => setPasso("password")} className="text-primary hover:underline">
              Entra con la password
            </button>
          </p>
        </div>
      )}
    </AuthLayout>
  );
}
